// src/pages/ClientDeliveries.js — Utilities → Client Deliveries.
// Staff create a delivery, drop N files (uploaded SEQUENTIALLY — no openSession-style burst), and copy a
// public /delivery/<token> link. ⛔ Upload is a per-file result list with failures called out BY NAME and a
// "Retry failed" that re-attempts ONLY the failures — a silent "97 of 100" is the failure mode that matters.
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { deliveryUpload } from '../utils/deliveryUpload';

async function apiPost(path, body) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session && session.access_token;
  if (!token) throw new Error('not signed in');
  const resp = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body || {}),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(json.error || String(resp.status));
  return json;
}

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; } };
const fmtSize = (b) => b == null ? '' : b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
const defaultExpiryValue = () => { const d = new Date(); d.setMonth(d.getMonth() + 8); return d.toISOString().slice(0, 10); };  // YYYY-MM-DD, 8 months out

// Access-history rows that record a FAILURE (api/delivery-* + lib/deliveryLog; migration 2026-10-08_delivery_access_log_detail).
const FAILURE_ACTIONS = new Set(['refused', 'error', 'client_error']);
const ACTION_LABEL = { client_error: 'browser error' };
const STAGE_LABEL = { view: 'opening the page', sign: 'preparing a file', batch: 'preparing the zip', fetch: 'fetching from storage', zip: 'building the zip', script: 'page error' };
const REASON_LABEL = { expired: 'link expired', revoked: 'link revoked', file_not_in_delivery: 'file not in this delivery', sign_failed: 'could not create the download link', file_lookup_failed: 'database error', file_list_failed: 'database error', network: 'request never reached the server' };
// One line saying why: "preparing a file — request never reached the server (Failed to fetch)", "link expired", …
function describeFailure(d) {
  if (!d || typeof d !== 'object') return '';
  if (d.reason) return REASON_LABEL[d.reason] || d.reason;
  const what = STAGE_LABEL[d.stage] || d.stage || 'error';
  const why = d.status === 0 ? (d.stage === 'fetch' ? 'blocked or unreachable' : REASON_LABEL.network) : d.status ? `HTTP ${d.status}` : (d.error || '');
  const n = Array.isArray(d.files_failed) && d.files_failed.length ? ` · ${d.files_failed.length} file(s)` : '';
  return [what, why].filter(Boolean).join(' — ') + n + (d.message ? ` (${d.message})` : '');
}

export default function ClientDeliveries({ darkMode }) {
  const c = darkMode
    ? { bg: '#0f172a', card: '#1e293b', text: '#e2e8f0', sub: '#94a3b8', muted: '#64748b', border: '#334155', accent: '#60a5fa', good: '#34d399', bad: '#f87171', input: '#0f172a' }
    : { bg: '#f8fafc', card: '#ffffff', text: '#0f172a', sub: '#475569', muted: '#94a3b8', border: '#e2e8f0', accent: '#2563eb', good: '#059669', bad: '#dc2626', input: '#ffffff' };

  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // create form
  const [label, setLabel] = useState('');
  const [expiry, setExpiry] = useState(defaultExpiryValue());
  const [creating, setCreating] = useState(false);

  // active delivery being filled
  const [active, setActive] = useState(null);           // the deliveries row we just created / picked
  const [staged, setStaged] = useState([]);             // [{ key, file, name }]
  const [results, setResults] = useState({});           // key -> { name, status:'queued'|'uploading'|'done'|'failed', error }
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // audit-log expansion (per delivery)
  const [expanded, setExpanded] = useState(null);       // delivery id whose log is open
  const [detail, setDetail] = useState({ files: [], access_log: [] });
  const [detailLoading, setDetailLoading] = useState(false);

  // Dedup: "name|size" already in the active delivery, so a re-drop (resumed batch) skips instead of duplicating.
  const [existingKeys, setExistingKeys] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    if (!active?.id) { setExistingKeys(new Set()); return; }
    (async () => {
      try { const r = await apiPost('/api/delivery-list', { delivery_id: active.id }); if (!cancelled) setExistingKeys(new Set((r.files || []).map((f) => `${f.file_name}|${f.size_bytes}`))); }
      catch { if (!cancelled) setExistingKeys(new Set()); }
    })();
    return () => { cancelled = true; };
  }, [active]);

  const refresh = useCallback(async () => {
    setLoading(true); setErr('');
    try { const { deliveries } = await apiPost('/api/delivery-list', {}); setDeliveries(deliveries || []); }
    catch (e) { setErr(`Couldn't load deliveries: ${e.message}`); }
    setLoading(false);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  async function createDelivery() {
    if (creating) return;
    setCreating(true); setErr('');
    try {
      const { delivery } = await apiPost('/api/delivery-create', {
        label: label.trim(),
        expires_at: expiry ? `${expiry}T23:59:59` : undefined,
      });
      setActive(delivery); setStaged([]); setResults({}); setLabel('');
      await refresh();
    } catch (e) { setErr(`Create failed: ${e.message}`); }
    setCreating(false);
  }

  function addFiles(fileList) {
    const add = Array.from(fileList).map((file, i) => ({ key: `${Date.now()}-${i}-${file.name}-${file.size}`, file, name: file.name }));
    setStaged((prev) => [...prev, ...add]);
    setResults((prev) => { const n = { ...prev }; add.forEach((s) => { n[s.key] = { name: s.name, status: 'queued' }; }); return n; });
  }

  async function runUploads(items) {
    if (!active || uploading || !items.length) return;
    setUploading(true);
    const seen = new Set(existingKeys);            // pre-existing files + ones uploaded this pass (intra-batch dups too)
    for (const s of items) {                       // ⛔ SEQUENTIAL — one sign→PUT→verify per file, no burst
      const dupKey = `${s.file.name}|${s.file.size}`;
      if (seen.has(dupKey)) { setResults((prev) => ({ ...prev, [s.key]: { name: s.name, status: 'skipped' } })); continue; }  // already in delivery — don't re-upload
      setResults((prev) => ({ ...prev, [s.key]: { name: s.name, status: 'uploading' } }));
      const { error, duplicate } = await deliveryUpload(active.id, s.file);
      if (duplicate) { seen.add(dupKey); setResults((prev) => ({ ...prev, [s.key]: { name: s.name, status: 'skipped' } })); continue; }  // server caught a dup a stale client set missed
      if (!error) seen.add(dupKey);
      setResults((prev) => ({ ...prev, [s.key]: { name: s.name, status: error ? 'failed' : 'done', error: error && error.message } }));
    }
    setExistingKeys(seen);
    setUploading(false);
    await refresh();                               // update the delivery's file count
  }

  async function toggleLog(d) {
    if (expanded === d.id) { setExpanded(null); return; }
    setExpanded(d.id); setDetailLoading(true);
    try { const r = await apiPost('/api/delivery-list', { delivery_id: d.id }); setDetail({ files: r.files || [], access_log: r.access_log || [] }); }
    catch (e) { setDetail({ files: [], access_log: [] }); setErr(`Couldn't load log: ${e.message}`); }
    setDetailLoading(false);
  }
  async function revoke(d) {
    const doRevoke = !d.revoked_at;
    if (!window.confirm(doRevoke
      ? `Revoke "${d.label || 'this delivery'}"? The link dies immediately for anyone who has it.`
      : `Restore "${d.label || 'this delivery'}"? The link works again.`)) return;
    try { await apiPost('/api/delivery-revoke', { delivery_id: d.id, revoked: doRevoke }); await refresh(); }
    catch (e) { setErr(`Revoke failed: ${e.message}`); }
  }
  async function deleteDelivery(d) {
    if (!window.confirm(`Permanently delete "${d.label || 'this delivery'}" and its ${d.fileCount} file${d.fileCount === 1 ? '' : 's'}? This cannot be undone and the client's link will stop working.`)) return;
    try {
      await apiPost('/api/delivery-delete', { delivery_id: d.id });
      if (expanded === d.id) setExpanded(null);
      if (active?.id === d.id) setActive(null);
      await refresh();
    } catch (e) { setErr(`Delete failed: ${e.message}`); }
  }

  const link = active ? `${window.location.origin}/delivery/${active.token}` : '';
  const doneCount = staged.filter((s) => results[s.key]?.status === 'done').length;
  const skippedCount = staged.filter((s) => results[s.key]?.status === 'skipped').length;
  const failedItems = staged.filter((s) => results[s.key]?.status === 'failed');
  const pending = (s) => !['done', 'skipped'].includes(results[s.key]?.status);
  const S = { input: { background: c.input, color: c.text, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '8px 10px', fontSize: '13px' },
              btn: { background: c.accent, color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
              ghost: { background: 'none', color: c.accent, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' } };

  return (
    <div style={{ padding: '24px 32px', color: c.text }}>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Client Deliveries</div>
      <div style={{ fontSize: '13px', color: c.sub, marginBottom: '20px' }}>Upload files, share a public download link. The link is a delivery only — anyone with it can download until it expires or you revoke it.</div>
      {err && <div style={{ background: darkMode ? '#3f1d1d' : '#fef2f2', color: c.bad, border: `1px solid ${c.bad}`, borderRadius: '6px', padding: '8px 12px', fontSize: '13px', marginBottom: '14px' }}>{err}</div>}

      {/* ── Create + fill a delivery ── */}
      <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '10px', padding: '16px', marginBottom: '24px' }}>
        {!active ? (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div><div style={{ fontSize: '11px', color: c.sub, marginBottom: '4px' }}>Label (for you)</div><input style={{ ...S.input, width: '260px' }} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. CB Budgets 2026" /></div>
            <div><div style={{ fontSize: '11px', color: c.sub, marginBottom: '4px' }}>Expires <span style={{ color: c.muted }}>(default 8 months)</span></div><input type="date" style={S.input} value={expiry} onChange={(e) => setExpiry(e.target.value)} /></div>
            <button style={{ ...S.btn, opacity: (creating || !label.trim()) ? 0.5 : 1 }} onClick={createDelivery} disabled={creating || !label.trim()} title={!label.trim() ? 'A label is required' : ''}>{creating ? 'Creating…' : '+ New Delivery'}</button>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div><strong>{active.label || 'Untitled delivery'}</strong> <span style={{ fontSize: '12px', color: c.sub }}>· Expires {fmtDate(active.expires_at)}</span></div>
              <button style={S.ghost} onClick={() => { setActive(null); setStaged([]); setResults({}); }}>Done</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
              <input readOnly value={link} style={{ ...S.input, flex: 1, minWidth: '280px', fontFamily: 'monospace', fontSize: '12px' }} onFocus={(e) => e.target.select()} />
              <button style={S.ghost} onClick={() => { navigator.clipboard.writeText(link); }}>Copy link</button>
            </div>
            <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                 onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                 style={{ border: `2px dashed ${dragOver ? c.accent : c.border}`, borderRadius: '8px', padding: '20px', textAlign: 'center', fontSize: '13px', color: c.sub, marginBottom: '12px' }}>
              Drag files here, or <label style={{ color: c.accent, cursor: 'pointer' }}>browse<input type="file" multiple style={{ display: 'none' }} onChange={(e) => addFiles(e.target.files)} /></label>
              {staged.length > 0 && <span> · {staged.length} staged</span>}
            </div>
            {staged.length > 0 && (
              <div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <button style={S.btn} onClick={() => runUploads(staged.filter(pending))} disabled={uploading}>{uploading ? 'Uploading…' : `Upload ${staged.filter(pending).length} file(s)`}</button>
                  {failedItems.length > 0 && !uploading && <button style={{ ...S.ghost, color: c.bad, borderColor: c.bad }} onClick={() => runUploads(failedItems)}>Retry {failedItems.length} failed</button>}
                  <span style={{ fontSize: '13px', color: c.sub, marginLeft: 'auto' }}>
                    <strong style={{ color: c.good }}>{doneCount}</strong> of {staged.length} uploaded{skippedCount > 0 && <span style={{ color: c.muted }}> · {skippedCount} skipped (already sent)</span>}{failedItems.length > 0 && <span style={{ color: c.bad }}> · {failedItems.length} failed</span>}
                  </span>
                </div>
                <div style={{ maxHeight: '260px', overflowY: 'auto', border: `1px solid ${c.border}`, borderRadius: '6px' }}>
                  {staged.map((s) => { const r = results[s.key] || {}; return (
                    <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderBottom: `1px solid ${c.border}`, fontSize: '12px' }}>
                      <span style={{ width: '16px' }}>{r.status === 'done' ? '✅' : r.status === 'failed' ? '⛔' : r.status === 'skipped' ? '⊘' : r.status === 'uploading' ? '⏳' : '•'}</span>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                      {r.status === 'failed' && <span style={{ color: c.bad, fontSize: '11px' }}>{r.error || 'failed'}</span>}
                      {r.status === 'skipped' && <span style={{ color: c.muted, fontSize: '11px' }}>already in delivery</span>}
                    </div>
                  ); })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Existing deliveries ── */}
      <div style={{ fontSize: '13px', fontWeight: 600, color: c.sub, marginBottom: '8px' }}>All deliveries</div>
      {loading ? <div style={{ color: c.muted, fontSize: '13px' }}>Loading…</div>
        : deliveries.length === 0 ? <div style={{ color: c.muted, fontSize: '13px' }}>No deliveries yet.</div>
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {deliveries.map((d) => {
              const revoked = !!d.revoked_at;
              const expired = new Date(d.expires_at).getTime() < Date.now();
              const dLink = `${window.location.origin}/delivery/${d.token}`;
              const isOpen = expanded === d.id;
              const dlCount = detail.access_log.filter((l) => l.action === 'download').length;
              const failCount = detail.access_log.filter((l) => FAILURE_ACTIONS.has(l.action)).length;
              return (
                <div key={d.id}>
                  <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: isOpen ? '8px 8px 0 0' : '8px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '8px', opacity: revoked || expired ? 0.6 : 1, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontWeight: 600 }}>{d.label || 'Untitled delivery'}
                        {revoked && <span style={{ color: c.bad, fontSize: '11px', marginLeft: '8px', fontWeight: 700 }}>REVOKED</span>}
                        {!revoked && expired && <span style={{ color: c.muted, fontSize: '11px', marginLeft: '8px', fontWeight: 700 }}>EXPIRED</span>}
                      </div>
                      <div style={{ fontSize: '12px', color: c.sub }}>Expires {fmtDate(d.expires_at)} · {d.fileCount} file{d.fileCount === 1 ? '' : 's'} · {d.downloadCount} download{d.downloadCount === 1 ? '' : 's'} · by {d.created_by}</div>
                    </div>
                    <button style={{ ...S.ghost, fontSize: '11px' }} onClick={() => { navigator.clipboard.writeText(dLink); }}>Copy link</button>
                    <button style={{ ...S.ghost, fontSize: '11px' }} onClick={() => { setActive(d); setStaged([]); setResults({}); }}>Add files</button>
                    <button style={{ ...S.ghost, fontSize: '11px' }} onClick={() => toggleLog(d)}>{isOpen ? 'Hide log' : 'Log'}</button>
                    <button style={{ ...S.ghost, fontSize: '11px', color: revoked ? c.good : c.bad, borderColor: revoked ? c.good : c.bad }} onClick={() => revoke(d)}>{revoked ? 'Restore' : 'Revoke'}</button>
                    <button style={{ ...S.ghost, fontSize: '13px', color: c.bad, borderColor: c.bad }} title="Delete permanently (files + record)" onClick={() => deleteDelivery(d)}>🗑</button>
                  </div>
                  {isOpen && (
                    <div style={{ background: c.card, border: `1px solid ${c.border}`, borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '10px 14px', marginBottom: '4px' }}>
                      {detailLoading ? <div style={{ fontSize: '12px', color: c.muted }}>Loading…</div> : (
                        <>
                          {/* What was sent — the answer to "what exactly did we deliver to this client?" */}
                          <div style={{ fontSize: '12px', fontWeight: 600, color: c.sub, marginBottom: '4px' }}>Files in this delivery ({detail.files.length})</div>
                          {detail.files.length > 0 && (
                            <div style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '11px', border: `1px solid ${c.border}`, borderRadius: '6px', marginBottom: '12px' }}>
                              {detail.files.map((f) => (
                                <div key={f.id} style={{ display: 'flex', gap: '8px', padding: '4px 8px', borderBottom: `1px solid ${c.border}` }}>
                                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</span>
                                  <span style={{ color: c.muted, whiteSpace: 'nowrap' }}>{fmtSize(f.size_bytes)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          <div style={{ fontSize: '12px', fontWeight: 600, color: c.sub, marginBottom: '4px' }}>Access history</div>
                          <div style={{ fontSize: '12px', color: c.sub, marginBottom: '6px' }}>
                            {detail.access_log.length === 0 ? 'No access yet.'
                              : <>Last access: <strong>{new Date(detail.access_log[0].at).toLocaleString()}</strong> · {dlCount} download{dlCount === 1 ? '' : 's'} logged
                                  {failCount > 0 && <span style={{ color: c.bad, fontWeight: 600 }}> · {failCount} failure{failCount === 1 ? '' : 's'}</span>}</>}
                          </div>
                          {detail.access_log.length > 0 && (
                            <div style={{ maxHeight: '220px', overflowY: 'auto', fontSize: '11px', border: `1px solid ${c.border}`, borderRadius: '6px' }}>
                              {detail.access_log.map((l, i) => (
                                <div key={i} title={l.user_agent || ''} style={{ display: 'flex', gap: '8px', padding: '4px 8px', borderBottom: `1px solid ${c.border}` }}>
                                  <span style={{ color: c.muted, whiteSpace: 'nowrap' }}>{new Date(l.at).toLocaleString()}</span>
                                  <span style={{ color: l.action === 'download' ? c.good : FAILURE_ACTIONS.has(l.action) ? c.bad : c.sub, width: '78px', fontWeight: FAILURE_ACTIONS.has(l.action) ? 600 : 400 }}>{ACTION_LABEL[l.action] || l.action}</span>
                                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {l.file_name || (l.detail ? '' : '—')}
                                    {l.detail && <span style={{ color: c.bad }}>{l.file_name ? ' — ' : ''}{describeFailure(l.detail)}</span>}
                                  </span>
                                  <span style={{ color: c.muted, whiteSpace: 'nowrap' }}>{l.ip || ''}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
